import { Link } from '@tanstack/react-router'
import type { HelpCategoryData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import {
  SecondaryButton,
  secondaryLinkClasses,
} from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminDeleteHelpCategoryMutation,
  useAdminReorderHelpArticlesMutation,
  useAdminUpdateHelpCategoryMutation,
} from '../../admin/hooks/use-admin-api'
import { CategoryForm } from './category-form'

interface CategorySectionProps {
  category: HelpCategoryData
  isFirst: boolean
  isLast: boolean
  isReordering: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onMutationError: (message: string) => void
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function CategorySection({
  category,
  isFirst,
  isLast,
  isReordering,
  onMoveUp,
  onMoveDown,
  onMutationError,
}: CategorySectionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const updateMutation = useAdminUpdateHelpCategoryMutation()
  const deleteMutation = useAdminDeleteHelpCategoryMutation()
  const reorderArticlesMutation = useAdminReorderHelpArticlesMutation()

  const moveArticle = (index: number, direction: -1 | 1) => {
    const orderedIds = category.articles.map((a) => a.id)
    const target = index + direction
    if (target < 0 || target >= orderedIds.length) return
    const current = orderedIds[index]
    const swapped = orderedIds[target]
    if (current === undefined || swapped === undefined) return
    orderedIds[index] = swapped
    orderedIds[target] = current
    reorderArticlesMutation.mutate(
      { body: { categoryId: category.id, orderedIds } },
      { onError: () => onMutationError('Failed to reorder articles') }
    )
  }

  const handleUpdate = (data: { name: string; description: string }) => {
    updateMutation.mutate(
      {
        path: { id: category.id },
        body: { name: data.name, description: data.description || null },
      },
      {
        onSuccess: () => setIsEditing(false),
        onError: () => onMutationError('Failed to update category'),
      }
    )
  }

  return (
    <section className="rounded-lg border border-border">
      {/* Category header */}
      <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
        {isEditing ? (
          <CategoryForm
            initialName={category.name}
            initialDescription={category.description ?? ''}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
            isSubmitting={updateMutation.isPending}
          />
        ) : (
          <>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold">{category.name}</h2>
              {category.description && (
                <p className="truncate text-xs text-muted-foreground">{category.description}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={onMoveUp}
                disabled={isFirst || isReordering}
                className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                aria-label="Move category up"
              >
                <ChevronUp className="size-4" />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={isLast || isReordering}
                className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                aria-label="Move category down"
              >
                <ChevronDown className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                aria-label="Edit category"
              >
                <Pencil className="size-4" />
              </button>
              {showDeleteConfirm ? (
                <>
                  <span className="text-xs text-red-400">Delete?</span>
                  <SecondaryButton
                    type="button"
                    tone="danger"
                    size="sm"
                    onClick={() =>
                      deleteMutation.mutate(
                        { path: { id: category.id } },
                        { onError: () => onMutationError('Failed to delete category') }
                      )
                    }
                    loading={deleteMutation.isPending}
                  >
                    Confirm
                  </SecondaryButton>
                  <SecondaryButton
                    type="button"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </SecondaryButton>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={category.articles.length > 0}
                  title={
                    category.articles.length > 0
                      ? 'Move or delete its articles first'
                      : 'Delete category'
                  }
                  className="rounded p-1 text-muted-foreground hover:text-red-400 disabled:opacity-30"
                  aria-label="Delete category"
                >
                  <Trash2 className="size-4" />
                </button>
              )}
              <Link
                to="/admin/help-center/new"
                search={{ categoryId: category.id }}
                className={cn('ml-2', secondaryLinkClasses({ size: 'sm' }))}
              >
                <Plus />
                <span>New article</span>
              </Link>
            </div>
          </>
        )}
      </div>

      {/* Articles */}
      {category.articles.length === 0 ? (
        <p className="px-4 py-3 text-sm text-muted-foreground">No articles yet</p>
      ) : (
        <ul>
          {category.articles.map((article, index) => (
            <li
              key={article.id}
              className="flex items-center justify-between gap-3 border-b border-border px-4 py-2 last:border-b-0"
            >
              <Link
                to="/admin/help-center/$id"
                params={{ id: article.id }}
                className="min-w-0 truncate text-sm hover:underline"
              >
                {article.title}
              </Link>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {formatDate(article.updatedAt)}
                </span>
                <Badge variant={article.status === 'PUBLISHED' ? 'default' : 'secondary'} size="sm">
                  {article.status === 'PUBLISHED' ? 'Published' : 'Draft'}
                </Badge>
                <button
                  type="button"
                  onClick={() => moveArticle(index, -1)}
                  disabled={index === 0 || reorderArticlesMutation.isPending}
                  className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label="Move article up"
                >
                  <ChevronUp className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveArticle(index, 1)}
                  disabled={
                    index === category.articles.length - 1 || reorderArticlesMutation.isPending
                  }
                  className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label="Move article down"
                >
                  <ChevronDown className="size-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
